import React, { useState, useEffect } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Product {
  [key: string]: {
    value: string;
    valueAlgo?: string;
    valueBulk?: string;
    valueConfidence?: string;
    valueConfidenceBulk?: string;
    valueConfidenceSource?: string;
    valueConfidenceSourceBulk?: string;
  };
}

interface Analysis {
  html_diag_QTY: string;
}

interface Process {
  analysis: Analysis;
}

interface Order {
  products: Product[];
}

interface OrderData {
  order: Order;
  process?: Process;
}

interface DebugFPProps {
  readonly index: number;
}

interface Params {
  [key: string]: string | undefined;
  orderId: string;
}

export default function DebugFP({ index }: DebugFPProps) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const params = useParams<Params>();
  const [data, setData] = useState<OrderData | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(index);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosPrivate.get(`/orders/${params.orderId}`);
        setData(response.data);
      } catch (error) {
        console.error(t("error_fetching_data"), error);
      }
    };

    fetchData();
  }, [params.orderId, axiosPrivate, t]);

  const renderProductInfo = (product: Product) => {
    const fields = [
      "internal_ocr_lines",
      "product_id",
      "product_name",
      "product_quantity",
      "product_variant",
      "product_price_ht",
      "product_price_total_ht",
      "product_packing"
    ];

    return fields.map((field, fieldIndex) => (
      <div key={field}>
        <h3 className="pt-5 font-mono">{field}</h3>
        {[
          "value",
          "valueAlgo",
          "valueBulk",
          "valueConfidence",
          "valueConfidenceBulk",
          "valueConfidenceSource",
          "valueConfidenceSourceBulk",
        ].map((subField, subFieldIndex) => (
          <div
            key={subField}
            style={{
              backgroundColor: subFieldIndex % 2 === 0 ? "#f3f1f4" : "#ffffff",
              padding: "8px",
            }}
          >
            <div className="text-xs">
              <b>{subField}</b>: {product[field]?.[subField] || t("not_applicable")}
            </div>
          </div>
        ))}
      </div>
    ));
  };

  const product = data?.order?.products?.[selectedIndex];
  const link_html_diag = data?.process?.analysis?.html_diag_QTY;

  return (
    <div className="flex">
      {data && (
        <div className="w-1/4 p-4 border-r">
          <h2 className="font-bold mb-4">{t("product_list")}</h2>
          <ul>
            {data.order.products.map((product, idx) => {
              const uniqueKey = `${product.product_id}-${idx}`;
              return (
                <li key={uniqueKey} className="mb-2">
                  <button
                    className={`cursor-pointer text-sm p-2 w-full text-left ${
                      selectedIndex === idx ? "bg-blue-200" : "bg-gray-100"
                    } hover:bg-blue-100`}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    {t("product")} {idx + 1} - {t("index")}: {idx}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="w-3/4 p-4">
        {data && product && (
          <>
            <div>
              <a
                href={`/orders/${params.orderId}`}
                className="text-blue-500 underline text-xs"
                target="_blanck"
              >
                {t("open_order")} : {params.orderId}
              </a>
            </div>
            {link_html_diag && (
              <div>
                <a
                  href={link_html_diag}
                  className="text-blue-500 underline text-xs"
                  target="_blanck"
                >
                  {t("view_analysis_link")}
                </a>
              </div>
            )}
            <div className="text-sm">{renderProductInfo(product)}</div>
          </>
        )}
      </div>
    </div>
  );
}